import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query'
import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  OpalApiResponseDocument,
  OpalApiRequestParams,
  boards,
} from '../../../../v3'
import { globalQueryKeys } from '../../queryKeys'

type ResponseDocument = OpalApiResponseDocument<'ReadBoardsV3', 200>
type NonPageQueryParams = Omit<
  OpalApiRequestParams<'ReadBoardsV3'>['query'],
  'page'
>

type UsePaginatedBoardsArgs<T = ResponseDocument> = NonPageQueryParams & {
  limit?: number
  select?: (document?: InfiniteData<ResponseDocument>) => T
  sort?: string
  workspaceId: string
}

const LIMIT = 100

type FetchBoardsParams = NonPageQueryParams & {
  page: number
  limit: number
  sort?: string
}

const fetchBoards = ({
  filter,
  fields,
  include,
  page,
  limit,
  sort,
}: FetchBoardsParams) =>
  boards.readCollection({
    params: {
      query: {
        filter,
        fields,
        include,
        sort,
        page: { offset: page * limit, limit },
      },
    },
  })

// Default sort is newest-first for better UX (backend defaults to asc for stable pagination)
const DEFAULT_SORT = '-created_at'

export const usePaginatedBoards = <T = ResponseDocument>({
  filter,
  fields,
  include,
  limit = LIMIT,
  select,
  sort = DEFAULT_SORT,
  workspaceId,
}: UsePaginatedBoardsArgs<T>) => {
  const [initiatedAllRequests, setInitiatedAllRequests] = useState(false)

  const shouldFetchNextPage = useCallback(
    (document: InfiniteData<ResponseDocument>) => {
      const offset = document.pages.length * limit
      if (!document.pages[0].meta) return false
      if (document.pages[0].meta.total > offset) {
        return true
      }

      setInitiatedAllRequests(true)
      return false
    },
    [limit]
  )

  const queryFilter = useMemo(
    () => ({
      ...filter,
      workspace_id: [workspaceId],
    }),
    [filter, workspaceId]
  )

  const queryFilterKey = JSON.stringify(queryFilter)

  // Reset pagination state when filter changes (e.g., switching tabs)
  useEffect(() => {
    setInitiatedAllRequests(false)
  }, [queryFilterKey])

  const { data, isFetchingNextPage, isFetching, fetchNextPage } =
    useInfiniteQuery({
      refetchInterval: false,
      queryKey: globalQueryKeys.boardsByWorkspace(workspaceId, {
        filter: queryFilter,
        fields,
        include,
        sort,
        page: { limit },
      }),
      queryFn: ({ pageParam }) =>
        fetchBoards({
          page: pageParam ?? 0,
          filter: queryFilter,
          fields,
          include,
          sort,
          limit,
        }),
      getNextPageParam: (_lastPage, pages) => pages.length,
    })

  const boards_ = useMemo(() => select?.(data), [select, data])
  const hasData = Boolean(
    boards_ && Array.isArray(boards_) && boards_.length > 0
  )
  const resultsObject = useMemo(
    () => ({
      // Show data as soon as we have any - don't wait for all pages
      // Include !isFetching check to show loading when query key changes
      fetchingComplete: hasData || (initiatedAllRequests && !isFetching),
      // True while fetching additional pages (not initial load)
      isFetchingMore: hasData && !initiatedAllRequests,
      // Compatibility with React Query's isFetching
      isFetching,
      // True once first page has loaded (compatibility with isFetchedAfterMount)
      isFetchedAfterMount: hasData,
      boards: boards_,
    }),
    [hasData, initiatedAllRequests, isFetching, boards_]
  )

  useEffect(() => {
    if (!data) return
    if (
      !initiatedAllRequests &&
      !isFetching &&
      !isFetchingNextPage &&
      shouldFetchNextPage(data)
    ) {
      fetchNextPage()
    }
  }, [
    initiatedAllRequests,
    isFetchingNextPage,
    isFetching,
    data,
    shouldFetchNextPage,
    fetchNextPage,
  ])

  return resultsObject
}
